import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { DatePicker } from "../components/ui/date-picker";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Eye,
  Phone,
  Mail,
  Star,
  Calendar,
  Award,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  da_visionare: { label: "Da visionare", cls: "bg-sky-100 text-sky-700" },
  in_valutazione: { label: "In valutazione", cls: "bg-amber-100 text-amber-700" },
  promosso: { label: "Promosso", cls: "bg-emerald-100 text-emerald-700" },
  scartato: { label: "Scartato", cls: "bg-slate-100 text-slate-600" },
};

const ROLES = [
  "Portiere",
  "Difensore centrale",
  "Terzino destro",
  "Terzino sinistro",
  "Mediano",
  "Mezz'ala",
  "Trequartista",
  "Esterno destro",
  "Esterno sinistro",
  "Attaccante",
  "Punta",
];

const empty = {
  first_name: "",
  last_name: "",
  current_team: "",
  role: "",
  phone: "",
  email: "",
  notes: "",
  birth_date: "",
  rating: null,
  next_observation_date: "",
  status: "da_visionare",
};

function StarRating({ value, onChange, readOnly }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange && onChange(value === n ? null : n)}
          className="disabled:cursor-default"
          title={`${n} stelle`}
        >
          <Star
            size={readOnly ? 14 : 18}
            className={
              n <= (value || 0)
                ? "fill-amber-400 text-amber-400"
                : "text-slate-300"
            }
          />
        </button>
      ))}
    </div>
  );
}

export default function ScoutingPage() {
  const { activeTeam, canEdit } = useTeams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all__");

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const { data } = await api.get(`/teams/${activeTeam.id}/scouts`);
    setItems(data);
  }, [activeTeam]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return items.filter((s) => {
      const text = `${s.first_name} ${s.last_name} ${s.current_team || ""} ${s.role || ""}`.toLowerCase();
      if (search && !text.includes(search.toLowerCase())) return false;
      if (statusFilter !== "__all__" && s.status !== statusFilter) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Nome e cognome richiesti");
      return;
    }
    const payload = {
      ...form,
      team_id: activeTeam.id,
      rating: form.rating || null,
      birth_date: form.birth_date || null,
      next_observation_date: form.next_observation_date || null,
    };
    if (editingId) await api.put(`/scouts/${editingId}`, payload);
    else await api.post(`/scouts`, payload);
    toast.success("Profilo salvato");
    setOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const handleNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const handleEdit = (s) => {
    setForm({
      first_name: s.first_name || "",
      last_name: s.last_name || "",
      current_team: s.current_team || "",
      role: s.role || "",
      phone: s.phone || "",
      email: s.email || "",
      notes: s.notes || "",
      birth_date: s.birth_date || "",
      rating: s.rating || null,
      next_observation_date: s.next_observation_date || "",
      status: s.status || "da_visionare",
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Eliminare ${s.first_name} ${s.last_name}?`)) return;
    await api.delete(`/scouts/${s.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="scouting-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Scouting</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            Ragazzi visionati
          </h1>
          <p className="text-slate-500">{items.length} profili nel database</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca per nome o squadra..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              data-testid="scouting-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="scouting-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti gli stati</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button
              onClick={handleNew}
              className="rounded-full bg-sky-500 hover:bg-sky-600"
              data-testid="new-scout-btn"
            >
              <Plus size={16} className="mr-1" /> Nuovo profilo
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((s) => {
          const meta = STATUS_META[s.status] || STATUS_META.da_visionare;
          return (
            <Card key={s.id} className="p-5 border-slate-200 hover:shadow-md transition" data-testid={`scout-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center font-heading font-bold shrink-0">
                  {(s.first_name?.[0] || "") + (s.last_name?.[0] || "")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">
                    {s.last_name} {s.first_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {s.role || "Ruolo n/d"} {s.current_team && `· ${s.current_team}`}
                  </div>
                  <div className="mt-2">
                    <StarRating value={s.rating} readOnly />
                  </div>
                </div>
                {canEdit && (
                  <div className="flex flex-col gap-0.5">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(s)} title="Modifica">
                      <Pencil size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(s)} title="Elimina">
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${meta.cls}`}>
                  {meta.label}
                </span>
                {s.next_observation_date && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <Eye size={11} /> {s.next_observation_date}
                  </span>
                )}
                {s.birth_date && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <Calendar size={11} /> {s.birth_date}
                  </span>
                )}
              </div>
              {(s.phone || s.email) && (
                <div className="mt-3 flex items-center gap-3 text-xs">
                  {s.phone && (
                    <a
                      href={`tel:${s.phone}`}
                      className="inline-flex items-center gap-1 text-slate-600 hover:text-sky-600"
                    >
                      <Phone size={11} /> {s.phone}
                    </a>
                  )}
                  {s.email && (
                    <a
                      href={`mailto:${s.email}`}
                      className="inline-flex items-center gap-1 text-slate-600 hover:text-sky-600 truncate"
                    >
                      <Mail size={11} /> {s.email}
                    </a>
                  )}
                </div>
              )}
              {s.notes && (
                <p className="mt-3 text-xs text-slate-500 line-clamp-2 italic border-l-2 border-slate-200 pl-2">
                  {s.notes}
                </p>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
            <Award size={28} className="mx-auto text-slate-300 mb-2" />
            {items.length === 0
              ? "Nessun ragazzo visionato. Aggiungi il primo profilo!"
              : "Nessun profilo corrisponde ai filtri."}
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica profilo" : "Nuovo ragazzo visionato"}</DialogTitle>
            <DialogDescription className="sr-only">
              Inserisci i dati di un ragazzo osservato durante le partite di scouting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="scout-firstname"
                />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="scout-lastname"
                />
              </div>
              <div>
                <Label>Squadra di provenienza</Label>
                <Input
                  value={form.current_team}
                  onChange={(e) => setForm({ ...form, current_team: e.target.value })}
                  placeholder="ASD ..."
                />
              </div>
              <div>
                <Label>Ruolo</Label>
                <Select value={form.role || "__none__"} onValueChange={(v) => setForm({ ...form, role: v === "__none__" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— n/d —</SelectItem>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data di nascita</Label>
                <DatePicker
                  value={form.birth_date}
                  onChange={(v) => setForm({ ...form, birth_date: v })}
                />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+39 333 ..."
                />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Valutazione</Label>
                <div className="h-10 flex items-center">
                  <StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
                </div>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="scout-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Prossima osservazione</Label>
                <DatePicker
                  value={form.next_observation_date}
                  onChange={(v) => setForm({ ...form, next_observation_date: v })}
                />
              </div>
              <div className="col-span-2">
                <Label>Note</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Punti di forza, aree di miglioramento, attitudine..."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-scout-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
